import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    Divider,
    Drawer,
    Grid,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import { isEqual } from 'lodash';
import { MuiColorInput } from 'mui-color-input';
import * as React from 'react';

import { UploadIcon } from '../components/actionIcons';
import DeleteIconButton from '../components/DeleteIconButton';
import FileActionRow from '../components/FileActionRow';
import FormActionButtons from '../components/FormActionButtons';
import TimeOfDayPicker from '../components/TimeOfDayPicker';
import { LEAVE_WITHOUT_SAVING_MESSAGE } from '../components/useLeavePrompt';

const LineSettingsDialog = ({ open, route, onSave, onClose, onDirtyChange }) => {
    const [form, setForm] = React.useState({
        name: '',
        color: '#007AFF',
        start_time: '',
        end_time: '',
        loop_duration: '',
        warning: '',
        frequency: '',
        breaks: [],
        kml: null,
        kml_filename: null,
        kml_file: undefined,
    });

    const [openedForm, setOpenedForm] = React.useState(null);
    const kmlInputRef = React.useRef(null);

    const isDirty = open && openedForm !== null && !isEqual(form, openedForm);

    React.useEffect(() => {
        if (onDirtyChange) onDirtyChange(isDirty);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDirty]);

    // X, CANCEL and the backdrop.
    const requestClose = () => {
        if (isDirty && !window.confirm(LEAVE_WITHOUT_SAVING_MESSAGE)) return;
        onClose();
    };

    React.useEffect(() => {
        if (open && route) {
            const opened = {
                name: route.name || '',
                color: route.color || '#007AFF',
                start_time: route.start_time || '',
                end_time: route.end_time || '',
                loop_duration: route.loop_duration != null ? Math.round(route.loop_duration / 60) : '',
                warning: route.warning || '',
                frequency: route.frequency || '',
                breaks: route.breaks || [],
                kml: route.kml || null,
                kml_filename: route.kml_filename || null,
                kml_file: route.kml_file,
            };
            setForm(opened);
            setOpenedForm(opened);
        } else if (open) {
            const opened = {
                name: '',
                color: '#007AFF',
                start_time: '',
                end_time: '',
                loop_duration: '',
                warning: '',
                frequency: '',
                breaks: [],
                kml: null,
                kml_filename: null,
                kml_file: undefined,
            };
            setForm(opened);
            setOpenedForm(opened);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, route?.id]);

    const addBreak = () => {
        setForm((prev) => ({
            ...prev,
            breaks: [...prev.breaks, { start_time: '', end_time: '' }],
        }));
    };

    const updateBreak = (index, field, value) => {
        setForm((prev) => ({
            ...prev,
            breaks: prev.breaks.map((b, i) => (i === index ? { ...b, [field]: value } : b)),
        }));
    };

    const removeBreak = (index) => {
        setForm((prev) => ({
            ...prev,
            breaks: prev.breaks.filter((_, i) => i !== index),
        }));
    };

    const handleKmlUpload = (ev) => {
        const file = ev.target.files?.[0];
        if (!file) return;
        // Set the file synchronously so it's available even if user clicks Save before
        // the async FileReader (used only for in-browser preview) finishes.
        setForm((prev) => ({
            ...prev,
            kml_file: file,
            kml_filename: file.name,
        }));
        const reader = new FileReader();
        reader.onload = () => {
            setForm((prev) => ({ ...prev, kml: reader.result }));
        };
        reader.readAsText(file);
        // Allow re-uploading the same file by resetting the input value
        ev.target.value = '';
    };

    const handleKmlClear = () => {
        setForm((prev) => ({ ...prev, kml: null, kml_filename: null, kml_file: null }));
    };

    const handleSave = () => {
        onSave({
            name: form.name,
            color: form.color,
            start_time: form.start_time || null,
            end_time: form.end_time || null,
            loop_duration: form.loop_duration ? parseInt(form.loop_duration, 10) * 60 : null,
            warning: form.warning || null,
            frequency: form.frequency || null,
            breaks: form.breaks.filter((b) => b.start_time && b.end_time),
            kml: form.kml,
            kml_filename: form.kml_filename,
            kml_file: form.kml_file,
        });
    };

    return (
        <Drawer anchor="right" open={open} onClose={requestClose} PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">{route?.id ? 'Line settings' : 'Add new line'}</Typography>
                    <IconButton onClick={requestClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
                    <TextField
                        variant="filled"
                        label="Line Name"
                        value={form.name}
                        onChange={(e) => { const { value } = e.target; setForm((prev) => ({ ...prev, name: value })); }}
                        fullWidth
                        size="small"
                    />

                    <MuiColorInput
                        variant="filled"
                        label="Line Color"
                        value={form.color}
                        onChange={(color) => setForm((prev) => ({ ...prev, color }))}
                        fallbackValue="#007AFF"
                        format="hex"
                        isAlphaHidden
                        fullWidth
                        size="small"
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TimeOfDayPicker
                                label="Start Time"
                                value={form.start_time}
                                onChange={(v) => setForm((prev) => ({ ...prev, start_time: v }))}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TimeOfDayPicker
                                label="End Time"
                                value={form.end_time}
                                onChange={(v) => setForm((prev) => ({ ...prev, end_time: v }))}
                            />
                        </Grid>
                    </Grid>

                    <TextField
                        variant="filled"
                        label="Loop Duration (min)"
                        type="number"
                        value={form.loop_duration}
                        onChange={(e) => { const { value } = e.target; setForm((prev) => ({ ...prev, loop_duration: value })); }}
                        fullWidth
                        size="small"
                    />

                    <TextField
                        variant="filled"
                        label="Warning"
                        value={form.warning}
                        onChange={(e) => { const { value } = e.target; setForm((prev) => ({ ...prev, warning: value })); }}
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                    />

                    <TextField
                        variant="filled"
                        label="Frequency"
                        value={form.frequency}
                        onChange={(e) => { const { value } = e.target; setForm((prev) => ({ ...prev, frequency: value })); }}
                        fullWidth
                        size="small"
                    />

                    <Divider />

                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Break</Typography>
                        {form.breaks.map((brk, idx) => (
                            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                                <TimeOfDayPicker
                                    label="Start Time"
                                    value={brk.start_time}
                                    onChange={(v) => updateBreak(idx, 'start_time', v)}
                                    textFieldProps={{ fullWidth: false }}
                                    sx={{ width: 150 }}
                                />
                                <TimeOfDayPicker
                                    label="End Time"
                                    value={brk.end_time}
                                    onChange={(v) => updateBreak(idx, 'end_time', v)}
                                    textFieldProps={{ fullWidth: false }}
                                    sx={{ width: 150 }}
                                />
                                <DeleteIconButton title="Remove break" onClick={() => removeBreak(idx)} />
                            </Box>
                        ))}
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={addBreak} size="small">
                            ADD BREAK BLOCK
                        </Button>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>KML</Typography>
                        {(() => {
                            const hasLocalKml = !!form.kml || form.kml_file instanceof File;
                            const hasRemoteKml = typeof form.kml_file === 'string' && form.kml_file.length > 0;
                            const hasAnyKml = hasLocalKml || hasRemoteKml;
                            const remoteFilename = hasRemoteKml
                                ? decodeURIComponent(form.kml_file.split('/').pop().split('?')[0])
                                : null;
                            const openPicker = () => kmlInputRef.current?.click();
                            // CP-5559: the same KML row as Trip Info / Itinerary: [UPLOAD KML FILE] until there is a
                            // file, then its name (downloads) + Replace + trash.
                            return (
                                <>
                                    <input ref={kmlInputRef} type="file" hidden accept=".kml,application/vnd.google-earth.kml+xml" onChange={handleKmlUpload} />
                                    {hasAnyKml ? (
                                        <FileActionRow
                                            name={form.kml_filename || remoteFilename || 'route.kml'}
                                            file={form.kml_file}
                                            onReplace={openPicker}
                                            onDelete={handleKmlClear}
                                            replaceTitle="Replace KML file"
                                            deleteTitle="Remove KML file"
                                        />
                                    ) : (
                                        <Button variant="outlined" startIcon={<UploadIcon />} onClick={openPicker}>
                                            Upload KML file
                                        </Button>
                                    )}
                                </>
                            );
                        })()}
                    </Box>
                </Box>

                <Box sx={{ borderTop: '1px solid #d8d8d8', p: 2, display: 'flex', gap: 1 }}>
                    {/* Not "Save": this only updates the line set form; its page SAVE persists it. */}
                    <FormActionButtons
                        mode="edit"
                        submitLabel="Apply"
                        submitIcon={null}
                        onSubmit={handleSave}
                        submitDisabled={!form.name}
                        onCancel={requestClose}
                        size="medium"
                        sx={{ flex: 1 }}
                    />
                </Box>
            </Box>
        </Drawer>
    );
};

export default LineSettingsDialog;
